import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonItem, IonLabel, IonSelect, IonSelectOption, IonModal, IonHeader, IonToolbar, IonTitle, IonCheckbox, IonContent } from '@ionic/react';
import { FC, useState } from 'react';
import { RecordingService } from './RecordingService';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useAsyncEffect } from '../../support/useAsyncEffect';
import { IRecording, IRecordingStatus } from './RecordingState';
import { DevSettings, DevSettingsWriter } from '../../support/DevSettings';
import { CloseIcon } from '../../theme/Icons';

export const RecordingDebugPage: FC = () => {
    const recordingService = useInstance(RecordingService);
    const devSettings = useInstance(DevSettings);
    const devSettingsWriter = useInstance(DevSettingsWriter);

    const [recordings, setRecordings] = useState<IRecording[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<IRecordingStatus>('upload-failed');
    const [showModal, setShowModal] = useState(false);

    useAsyncEffect(async () => {
        setRecordings(await recordingService.getAll());
    }, [recordingService]);

    const deleteLocalRecordings = async () => {
        const all = await recordingService.getAll();
        all.forEach(async (recording) => await recordingService.delete(recording.id));
        setRecordings(await recordingService.getAll());
    };

    const handleInsertTestRecording = async () => {
        try {
            await recordingService.addTestRecording({
                status: selectedStatus,
                msDuration: 120000,
                transcription: 'Test transcription for development'
            });
            setRecordings(await recordingService.getAll());
            setShowModal(false);
        } catch (error) {
            console.error('Failed to insert test recording:', error);
        }
    };

    const handleToggleProcessing = async (checked: boolean) => {
        await devSettingsWriter.setSetting('disableRecordingProcessing', checked);
    };

    return (
        <>
            <h1>Recordings Debug Page</h1>

            <IonButton expand="block" onClick={() => setShowModal(true)}>
                Open Test Recording Tools
            </IonButton>

            <IonCard>
                <IonCardHeader>
                    <IonCardTitle>IndexedDB Recordings</IonCardTitle>
                </IonCardHeader>

                <IonCardContent>
                    {recordings.map(recording => (
                        <pre key={recording.id}>{JSON.stringify(recording, null, 2)}</pre>
                    ))}

                </IonCardContent>
            </IonCard>
            {recordings.length > 0 && <IonButton onClick={deleteLocalRecordings}>Delete Local Recordings</IonButton>}

            <IonModal isOpen={showModal} onDidDismiss={() => setShowModal(false)}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>Test Recording Tools</IonTitle>
                        <IonButton slot="end" fill="clear" onClick={() => setShowModal(false)}>
                            <CloseIcon />
                        </IonButton>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="ion-padding">
                    <IonCard>
                        <IonCardContent>
                            <IonItem lines="none" style={{marginBottom: '8px'}}>
                                <IonCheckbox
                                    checked={devSettings.disableRecordingProcessing}
                                    onIonChange={(e) => handleToggleProcessing(e.detail.checked)}
                                />
                                <IonLabel style={{marginLeft: '8px'}}>Disable Auto-Processing</IonLabel>
                            </IonItem>

                            <IonItem lines="none" style={{marginBottom: '12px'}}>
                                <IonLabel>Status:</IonLabel>
                                <IonSelect
                                    value={selectedStatus}
                                    onIonChange={(e) => setSelectedStatus(e.detail.value)}
                                >
                                    <IonSelectOption value="upload-failed">Upload Failed</IonSelectOption>
                                    <IonSelectOption value="recording-complete">Recording Complete</IonSelectOption>
                                    <IonSelectOption value="offline-pending">Offline Pending</IonSelectOption>
                                    <IonSelectOption value="uploading">Uploading</IonSelectOption>
                                    <IonSelectOption value="uploaded">Uploaded</IonSelectOption>
                                    <IonSelectOption value="transcribe-failed">Transcribe Failed</IonSelectOption>
                                </IonSelect>
                            </IonItem>

                            <IonButton
                                expand="block"
                                onClick={handleInsertTestRecording}
                                data-id="insert-test-recording-button"
                            >
                                Insert Test Recording
                            </IonButton>
                        </IonCardContent>
                    </IonCard>
                </IonContent>
            </IonModal>
        </>
    );
};
